import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { rupee } from "@/lib/api";
import { Plus, Minus, Trash2, ShoppingBag, Store } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { EmptyState } from "@/components/States";

export default function CartDrawer({ open, onClose }) {
  const { cart, subtotal, inc, dec, remove, count } = useCart();
  const nav = useNavigate();
  const deliveryFee = subtotal >= 499 ? 0 : (cart.store?.delivery_fee || 25);

  const checkout = () => { onClose(); nav("/checkout"); };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent data-testid="cart-drawer-sheet" className="flex w-full flex-col p-0 sm:max-w-md">
        <SheetHeader className="border-b border-slate-100 p-4">
          <SheetTitle className="flex items-center gap-2 font-head">
            <ShoppingBag className="h-5 w-5 text-brand" /> Your Cart {count > 0 && `(${count})`}
          </SheetTitle>
        </SheetHeader>

        {cart.items.length === 0 ? (
          <EmptyState icon={ShoppingBag} title="Your cart is empty" subtitle="Add items from a store to get started." />
        ) : (
          <>
            <div className="flex items-center gap-2 border-b border-slate-100 bg-brand-soft/50 px-4 py-2 text-sm">
              <Store className="h-4 w-4 text-brand" />
              <span className="font-semibold text-slate-700">{cart.store?.name}</span>
            </div>
            <div className="flex-1 space-y-3 overflow-auto p-4">
              {cart.items.map((it) => (
                <div key={it.product_id} data-testid="cart-item-row" className="flex items-center gap-3 rounded-xl border border-slate-100 p-2">
                  <img src={it.image} alt="" className="h-14 w-14 rounded-lg object-cover" />
                  <div className="flex-1">
                    <p className="line-clamp-1 text-sm font-semibold text-slate-800">{it.name}</p>
                    <p className="text-xs text-slate-400">{it.unit}</p>
                    <p className="font-mono text-sm font-bold text-slate-900">{rupee(it.price)}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-2 rounded-lg bg-brand px-1.5 py-1 text-white">
                      <button onClick={() => dec(it.product_id)}><Minus className="h-3.5 w-3.5" /></button>
                      <span className="min-w-[16px] text-center text-xs font-bold">{it.qty}</span>
                      <button onClick={() => inc(it.product_id)}><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                    <button data-testid="cart-item-remove-btn" onClick={() => remove(it.product_id)} className="text-slate-300 hover:text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-2 border-t border-slate-100 p-4">
              {subtotal < 499 ? (
                <div data-testid="cart-free-delivery-progress" className="mb-1 rounded-xl bg-brand-soft/60 p-2.5">
                  <p className="text-[11px] font-semibold text-brand">Add {rupee(499 - subtotal)} more for FREE delivery</p>
                  <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white"><div className="h-full rounded-full bg-brand transition-all" style={{ width: `${Math.min(100, Math.round((subtotal / 499) * 100))}%` }} /></div>
                </div>
              ) : (
                <p className="mb-1 rounded-xl bg-brand-soft/60 p-2.5 text-[11px] font-bold text-brand">🎉 You've unlocked FREE delivery</p>
              )}
              <Row label="Subtotal" value={rupee(subtotal)} testid="cart-subtotal-amount" />
              <Row label="Delivery fee" value={deliveryFee === 0 ? "FREE" : rupee(deliveryFee)} testid="cart-delivery-fee-amount" />
              <div className="flex items-center justify-between border-t border-dashed border-slate-200 pt-2">
                <span className="font-head font-bold text-slate-900">Total</span>
                <span data-testid="cart-total-amount" className="font-mono text-lg font-bold text-brand">{rupee(subtotal + deliveryFee + 5)}</span>
              </div>
              <Button data-testid="cart-proceed-checkout-btn" onClick={checkout}
                className="mt-2 w-full rounded-xl bg-brand py-6 text-base font-bold hover:bg-brand-deep">
                Proceed to Checkout
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

const Row = ({ label, value, testid }) => (
  <div className="flex items-center justify-between text-sm text-slate-600">
    <span>{label}</span>
    <span data-testid={testid} className="font-mono font-medium text-slate-800">{value}</span>
  </div>
);
